import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, CircleNotch, Clock, Envelope, Package } from "@phosphor-icons/react";
import { getOrder } from "@/lib/orders.functions";
import { Skeleton } from "@/components/ui/skeleton";
import bananaLogo from "@/assets/banana-cards-logo.png";

function EmailReminderBox({ className }: { className?: string }) {
  return (
    <div className={`rounded-2xl bg-muted px-4 py-4 sm:px-5 sm:py-5 flex items-start gap-3 ${className ?? ""}`}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
        <Envelope className="h-4 w-4" weight="fill" />
      </div>
      <div>
        <div className="font-bold text-sm sm:text-base">Important — check your email and spam folder</div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">
          We sent an order confirmation email with your order info. If you find it in spam, please click "Move to inbox" so you can keep receiving order updates from us.
        </div>
      </div>
    </div>
  );
}


export const Route = createFileRoute("/orders/$id_/status")({
  component: OrderStatusPage,
});

type Step = {
  key: string;
  label: string;
  description: string;
};

const STEPS_DESIGN: Step[] = [
  { key: "order_placed", label: "Order placed", description: "We received your order." },
  { key: "designs_submitted", label: "Designs submitted", description: "Your card designs are in our queue." },
  { key: "designs_approved", label: "Designs approved", description: "Our team has reviewed and approved your designs." },
  { key: "in_production", label: "In production", description: "Your cards are being crafted." },
  { key: "ready_to_ship", label: "Ready to ship", description: "Printed and packed, waiting for the courier." },
  { key: "shipped", label: "Shipped", description: "On the way to you." },
];

const STEPS_NO_DESIGN: Step[] = [
  { key: "order_placed", label: "Order placed", description: "We received your order." },
  { key: "ready_to_ship", label: "Ready to ship", description: "Your cards are packed and waiting for the courier." },
  { key: "shipped", label: "Shipped", description: "On the way to you." },
];

// Maps the DB `status` column to the index of the latest *completed* step.
function statusToIndexDesign(status: string | undefined): number {
  switch (status) {
    case "shipped":
    case "delivered":
      return 5;
    case "printed":
    case "ready_to_ship":
      return 4;
    case "printing":
    case "in_production":
      return 3;
    case "design_approved":
    case "designs_approved":
      return 2;
    case "to_be_printed":
    case "designs_submitted":
      return 1;
    default:
      return 0; // order placed
  }
}

function statusToIndexNoDesign(status: string | undefined): number {
  switch (status) {
    case "shipped":
    case "delivered":
      return 2;
    case "ready_to_ship":
    case "printed":
    case "to_be_printed":
    case "printing":
    case "in_production":
      return 1;
    default:
      return 0;
  }
}

function formatDate(iso?: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function OrderStatusPage() {
  const { id } = Route.useParams();
  const fetchOrder = useServerFn(getOrder);
  const { data, isLoading } = useQuery({
    queryKey: ["order", id],
    queryFn: () => fetchOrder({ data: { id } }),
  });

  const order = data?.order as
    | (Record<string, any> & {
        order_number: string;
        status: string;
        created_at: string;
        paid_at?: string | null;
        updated_at: string;
        status_history?: Array<{ status: string; at: string }> | null;
        card_instances?: Array<{ product_slug: string }> | null;
      })
    | undefined;

  const NON_CUSTOM = new Set(["google-review"]);
  const requiresDesign = Array.isArray(order?.card_instances)
    ? order!.card_instances!.some((ci) => !NON_CUSTOM.has(ci.product_slug))
    : true;
  const STEPS = requiresDesign ? STEPS_DESIGN : STEPS_NO_DESIGN;
  const currentIndex = requiresDesign
    ? statusToIndexDesign(order?.status)
    : statusToIndexNoDesign(order?.status);
  const history = Array.isArray(order?.status_history) ? order!.status_history! : [];

  function timeForStep(stepIdx: number): string | null {
    if (!order) return null;
    // Try status_history first
    const designKeys: string[][] = [
      ["order_placed", "created"],
      ["to_be_printed", "designs_submitted"],
      ["design_approved", "designs_approved"],
      ["printing", "in_production"],
      ["printed", "ready_to_ship"],
      ["shipped"],
    ];
    const noDesignKeys: string[][] = [
      ["order_placed", "created"],
      ["ready_to_ship", "printed", "to_be_printed"],
      ["shipped"],
    ];
    const keys = requiresDesign ? designKeys : noDesignKeys;
    const match = history.find((h) => keys[stepIdx].includes(h.status));
    if (match) return formatDate(match.at);
    // Fallbacks from base columns
    if (stepIdx === 0) return formatDate(order.paid_at || order.created_at);
    if (stepIdx === currentIndex) return formatDate(order.updated_at);
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={bananaLogo} alt="banana.cards" className="h-3.5" />
          </Link>
          {order && (
            <div className="text-[11px] sm:text-xs text-muted-foreground truncate">
              <span className="hidden sm:inline">Order </span>
              <span className="font-mono font-bold text-foreground">{order.order_number}</span>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-2">Order status</h1>
          <p className="text-[13px] sm:text-sm text-muted-foreground">
            Track your order from submission to delivery.
          </p>
        </div>

        <EmailReminderBox className="mb-6 sm:mb-8" />

        {isLoading || !order ? (
          <div className="space-y-4">
            {STEPS.map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2 py-1">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ol className="relative">
            {STEPS.map((step, idx) => {
              const completed = idx < currentIndex;
              const current = idx === currentIndex;
              const time = timeForStep(idx);
              const isLast = idx === STEPS.length - 1;
              return (
                <li key={step.key} className="relative flex gap-4 pb-8 last:pb-0">
                  {!isLast && (
                    <span
                      aria-hidden
                      className={`absolute left-[18px] top-9 bottom-0 w-px ${
                        completed ? "bg-foreground" : "bg-border"
                      }`}
                    />
                  )}
                  <div
                    className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${
                      completed
                        ? "bg-foreground border-foreground text-background"
                        : current
                          ? "bg-background border-foreground text-foreground"
                          : "bg-background border-border text-muted-foreground"
                    }`}
                  >
                    {completed ? (
                      <CheckCircle weight="fill" className="h-5 w-5" />
                    ) : current ? (
                      <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
                    ) : step.key === "shipped" ? (
                      <Package className="h-4 w-4" />
                    ) : (
                      <Clock className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                      <div
                        className={`font-bold text-sm sm:text-base ${
                          completed || current ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {step.label}
                      </div>
                      {current && (
                        <span className="text-[10px] uppercase tracking-wider font-bold rounded-full bg-foreground text-background px-2 py-0.5">
                          In progress
                        </span>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {step.description}
                    </div>
                    {time && (
                      <div className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-mono">
                        {time}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {order && (
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/orders/$id/customize"
              params={{ id }}
              className="inline-flex items-center justify-center rounded-full border border-border bg-card px-5 py-2.5 text-sm font-bold hover:bg-muted transition"
            >
              View designs
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition"
            >
              Back to home
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}